import React, {
  ChangeEvent,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
  memo,
} from 'react';
import './Content.css';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import UndoIcon from '@mui/icons-material/Undo';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import ImageAddDialog, {
  type ImageAddCandidate,
} from '../../common/ImageAddDialog/ImageAddDialog';
import ImageAttachmentsGrid from '../../common/ImageAddDialog/ImageAttachmentsGrid';
import type { ImageAttachment } from '../../../types/imageAttachment';


/** ---- API 戻り値の型（大小文字やネスト差を許容EE---- */
interface SearchResult {
  [key: string]: any;
  TITLE?: string; BODY?: string; title?: string; body?: string;
  feedback_body?: string; FEEDBACK_BODY?: string; fbNote?: string;
  feedback?: Array<{ feedback_body?: string; [k: string]: any }> | { feedback_body?: string };
  wr_id?: number; WR_ID?: number;
  start_date?: string; START_DATE?: string; end_date?: string; END_DATE?: string;
  create_by?: string; CREATE_BY?: string;
}

interface ValidationErrors {
  reporter?: string;
  startDate?: string;
  endDate?: string;
  title?: string;
  body?: string;
}

interface HistoryItem {
  title: string;
  body: string;
  feedback_body?: string | null;
  start_date: string;
  end_date: string;
}

interface Props {
  division: string;
  activeTab: 'new' | 'overwrite';

  draftTitle: string; setDraftTitle: (v: string) => void;
  draftBody: string; setDraftBody: (v: string) => void;

  title: string; setTitle: (v: string) => void;
  body: string; setBody: (v: string) => void;

  fbNote: string; setFbNote: (v: string) => void;

  setWrId: (v?: number) => void;
  setStartDate: (v: string) => void;
  setEndDate: (v: string) => void;
  setReporter: (v: string) => void;

  enlightSyncEnabled: boolean;
  autoSaveEnabled: boolean;

  searchResults?: SearchResult[];
  searchPerformed: boolean;

  validationErrors: ValidationErrors;
  historyItems?: HistoryItem[];

  currentWrId?: number;

  modalPinned?: boolean;
  modalPinnedWrId?: number;

  /** 斁E数カウンターEEnputEdit で計算！E*/
  remainingChars: number;
  charCountColor: 'hazard' | 'warning' | '';

  images: ImageAttachment[];
  onAddImageCandidates: (candidates: ImageAddCandidate[]) => void;
  onRemoveImage: (id: string) => void;

  footerSlot?: React.ReactNode;
}

type NormalizedResult = {
  uid: string;
  title: string;
  body: string;
  feedback?: string;
  wrId?: number;
  startDate: string;
  endDate: string;
  createBy: string;
  raw: SearchResult;
};

/* ---- F/B 抜き出ぁE---- */
function pickFirstFeedbackText(input: unknown): string {
  if (!input) return '';
  if (typeof input === 'string') return input;

  if (typeof input === 'object' && !Array.isArray(input)) {
    const obj = input as Record<string, any>;
    const v = obj.feedback_body ?? obj.FEEDBACK_BODY ?? '';
    return typeof v === 'string' ? v : '';
  }

  if (Array.isArray(input)) {
    const first = input[0];
    if (!first) return '';
    if (typeof first === 'string') return first;
    if (typeof first === 'object') {
      const v =
        (first as Record<string, any>).feedback_body ??
        (first as Record<string, any>).FEEDBACK_BODY ?? '';
      return typeof v === 'string' ? v : '';
    }
  }

  return '';
}

/* ---- wr_id 重褁E除 + 一段フラチE匁E---- */
function flattenAndDedupByWrId(input: unknown): SearchResult[] {
  let result: SearchResult[] = [];
  if (Array.isArray(input)) {
    const flattened: any[] = Array.isArray((input as any[])[0])
      ? (input as any[]).flat(1)
      : (input as any[]);
    const seen = new Set<string>();
    for (const r of flattened) {
      const wrId = (r?.wr_id ?? r?.WR_ID) as number | undefined;
      const title = (r?.title ?? r?.TITLE ?? '') as string;
      const sd = (r?.start_date ?? r?.START_DATE ?? '') as string;
      const ed = (r?.end_date ?? r?.END_DATE ?? '') as string;
      const key = wrId != null ? `wr:${wrId}` : `noid:${title}|${sd}|${ed}`;
      if (!seen.has(key)) {
        seen.add(key);
        result.push(r as SearchResult);
      }
    }
  }
  return result;
}

/* ---- 安宁Euid の払い出ぁE---- */
const useStableUid = () => {
  const mapRef = useRef<Map<string, string>>(new Map());

  const canonicalKeyOf = useCallback((r: SearchResult) => {
    const id = (r.wr_id ?? r.WR_ID);
    if (id != null) return `wr:${id}`;
    const sd = (r.start_date ?? r.START_DATE ?? '') as string;
    const ed = (r.end_date ?? r.END_DATE ?? '') as string;
    const cb = (r.create_by ?? r.CREATE_BY ?? '') as string;
    return `noid:${sd}|${ed}|${cb}`;
  }, []);

  const getUid = useCallback((r: SearchResult) => {
    const ck = canonicalKeyOf(r);
    if (ck.startsWith('wr:')) return ck.replace('wr:', 'wr-');
    const m = mapRef.current;
    const cached = m.get(ck);
    if (cached) return cached;
    const uid = `tmp-${m.size + 1}-${Math.random().toString(36).slice(2)}`;
    m.set(ck, uid);
    return uid;
  }, [canonicalKeyOf]);

  return { getUid, canonicalKeyOf };
};

/* 値が更新された瞬間だぁE“保存中 EパルスをEす簡易フチE */
const useSavingPulse = (value: string, delayMs = 150) => {
  const [saving, setSaving] = useState(false);
  const timerRef = useRef<number | null>(null);
  useEffect(() => {
    setSaving(true);
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => setSaving(false), delayMs);
    return () => { if (timerRef.current) window.clearTimeout(timerRef.current); };
  }, [value, delayMs]);
  return saving;
};

const manualMinHeightMap = new WeakMap<HTMLTextAreaElement, number>();
const resizeStartHeightMap = new WeakMap<HTMLTextAreaElement, number>();
const lastAutoHeightMap = new WeakMap<HTMLTextAreaElement, number>();

const resolveLineHeightPx = (style: CSSStyleDeclaration): number => {
  const fontSize = parseFloat(style.fontSize);
  const baseFontSize = Number.isFinite(fontSize) && fontSize > 0 ? fontSize : 16;
  const rawLineHeight = style.lineHeight;

  if (rawLineHeight && rawLineHeight !== 'normal') {
    if (rawLineHeight.endsWith('px')) {
      const px = parseFloat(rawLineHeight);
      if (Number.isFinite(px) && px > 0) return px;
    }
    const unitless = parseFloat(rawLineHeight);
    if (Number.isFinite(unitless) && unitless > 0) {
      return unitless * baseFontSize;
    }
  }

  return baseFontSize * 1.2;
};

const getBaselineMinHeight = (el: HTMLTextAreaElement, style: CSSStyleDeclaration): number => {
  const minHeightFromCss = parseFloat(style.minHeight);
  if (Number.isFinite(minHeightFromCss) && minHeightFromCss > 0) {
    return minHeightFromCss;
  }

  const rows = Math.max(1, el.rows || 1);
  const lineHeight = resolveLineHeightPx(style);
  const paddingTop = parseFloat(style.paddingTop) || 0;
  const paddingBottom = parseFloat(style.paddingBottom) || 0;
  return (lineHeight * rows) + paddingTop + paddingBottom;
};

const autoResizeTextarea = (el: HTMLTextAreaElement | null) => {
  if (!el) return;

  const style = window.getComputedStyle(el);
  let minHeight = getBaselineMinHeight(el, style);

  const prevHeight = el.offsetHeight;
  const lastAutoHeight = lastAutoHeightMap.get(el);
  if (typeof lastAutoHeight === 'number' && Math.abs(prevHeight - lastAutoHeight) >= 2) {
    manualMinHeightMap.set(el, prevHeight);
    el.style.minHeight = `${prevHeight}px`;
  }

  const manualMinHeight = manualMinHeightMap.get(el) ?? 0;
  if (manualMinHeight > 0) {
    minHeight = Math.max(minHeight, manualMinHeight);
    el.style.minHeight = `${minHeight}px`;
  }
  // CSS 側の min-height: 0 を上書きして 5行相当を固定
  el.style.minHeight = `${minHeight}px`;

  el.style.overflowY = 'hidden';
  el.style.maxHeight = 'none';

  const prevScrollTop = el.scrollTop;
  el.style.height = 'auto';
  const nextHeight = Math.max(el.scrollHeight, minHeight);
  el.style.height = `${nextHeight}px`;
  lastAutoHeightMap.set(el, nextHeight);
  if (prevScrollTop > 0 && el.scrollTop !== prevScrollTop) {
    el.scrollTop = prevScrollTop;
  }
};

const finalizeManualResize = (el: HTMLTextAreaElement | null) => {
  if (!el) return;
  const start = resizeStartHeightMap.get(el);
  resizeStartHeightMap.delete(el);
  const end = el.offsetHeight;
  if (typeof start === 'number' && Math.abs(end - start) >= 2) {
    manualMinHeightMap.set(el, end);
  }
};

const handleResizeStart = (e: React.SyntheticEvent<HTMLTextAreaElement>) => {
  const el = e.currentTarget as HTMLTextAreaElement | null;
  if (!el) return;
  resizeStartHeightMap.set(el, el.offsetHeight);
  const onUp = () => finalizeManualResize(el);
  window.addEventListener('pointerup', onUp, { once: true, capture: true });
  window.addEventListener('mouseup', onUp, { once: true, capture: true });
};

const handleResizeEnd = (e: React.SyntheticEvent<HTMLTextAreaElement>) => {
  finalizeManualResize(e.currentTarget as HTMLTextAreaElement | null);
};

const useAutoResizeTextarea = (value: string) => {
  const ref = useRef<HTMLTextAreaElement | null>(null);

  const resize = useCallback((target?: HTMLTextAreaElement | null) => {
    autoResizeTextarea(target ?? ref.current);
  }, []);

  const setRef = useCallback((node: HTMLTextAreaElement | null) => {
    ref.current = node;
    if (node) resize(node);
  }, [resize]);

  useLayoutEffect(() => {
    resize();
  }, [value, resize]);

  const onResizeStart = useCallback(
    (e: React.SyntheticEvent<HTMLTextAreaElement>) => handleResizeStart(e),
    []
  );
  const onResizeEnd = useCallback(
    (e: React.SyntheticEvent<HTMLTextAreaElement>) => handleResizeEnd(e),
    []
  );

  return { ref: setRef, resize, onResizeStart, onResizeEnd };
};

/* ============== Overwrite: 褁Eヒット用 ============== */
const OverwriteCard = memo<{
  result: NormalizedResult;
  active: boolean;
  onActivate: () => void;
  onCommitTitle: (v: string) => void;
  onCommitBody: (v: string) => void;
  SpinnerOrSync: (spinning: boolean) => React.ReactNode;
  remainingChars: number;
  charCountColor: 'hazard' | 'warning' | '';
}>(({
  result, active, onActivate, onCommitTitle, onCommitBody, SpinnerOrSync,
  remainingChars, charCountColor,
}) => {
  const [titleLocal, setTitleLocal] = useState(result.title ?? '');
  const [bodyLocal, setBodyLocal] = useState(result.body ?? '');
  const {
    ref: bodyRef,
    resize: resizeBody,
    onResizeStart: onBodyResizeStart,
    onResizeEnd: onBodyResizeEnd,
  } = useAutoResizeTextarea(bodyLocal);

  useEffect(() => { setTitleLocal(result.title ?? ''); }, [result.title]);
  useEffect(() => { setBodyLocal(result.body ?? ''); }, [result.body]);

  const savingTitle = useSavingPulse(titleLocal);
  const savingBody  = useSavingPulse(bodyLocal);

  const charColor =
    charCountColor === 'hazard'
      ? '#f44336'
      : charCountColor === 'warning'
      ? '#ffb300'
      : 'inherit';

  return (
    <div
      className="content-card content-card--list"
      onClick={onActivate}
    >
      <div className="content-grid">
        <div className="content-main">
          <div className="form-field">
            <div className="start-date-icon">
              <label className="required-label">タイトル</label>
              &nbsp;{SpinnerOrSync(active && savingTitle)}
            </div>
            {/* タイトルめE行textareaに変更 */}
            <textarea
              rows={2}
              className="title-textarea search-result-input"
              value={titleLocal}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
                setTitleLocal(e.target.value);
                onCommitTitle(e.target.value);
              }}
            />
          </div>

          <div className="form-field">
            <div className="start-date-icon">
              <label className="required-label">本文</label>
              &nbsp;{SpinnerOrSync(active && savingBody)}
              {active && (
                <span style={{ marginLeft: 8, fontWeight: 600, color: charColor }}>
                  {remainingChars >= 0 ? '残り ' : '文字数超過 '}
                  <span>{remainingChars}</span>
                </span>
              )}
            </div>
            <textarea
              className="body-textarea search-result-textarea"
              rows={5}
              ref={bodyRef}
              onPointerDown={onBodyResizeStart}
              onPointerUp={onBodyResizeEnd}
              value={bodyLocal}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
                setBodyLocal(e.target.value);
                onCommitBody(e.target.value);
                resizeBody(e.currentTarget);
              }}
            />
          </div>
        </div>

        <div className="content-side">
          <div className="form-field">
            <div className="start-date-icon-fb">
              <label>F/B事項</label>
            </div>
            <textarea
              className="fb-textarea search-result-textarea"
              rows={5}
              value={result.feedback ?? ''}
              readOnly
            />
          </div>
        </div>
      </div>
    </div>
  );
});
OverwriteCard.displayName = 'OverwriteCard';

/* ======================== メイン ======================== */
const Content: React.FC<Props> = (props) => {
  const {
    division, activeTab,
    draftTitle, setDraftTitle, draftBody, setDraftBody,
    title, setTitle, body, setBody,
    fbNote, setFbNote,
    setWrId, setStartDate, setEndDate, setReporter,
    enlightSyncEnabled, searchResults, searchPerformed,
    historyItems = [], modalPinned = false, modalPinnedWrId,
    remainingChars, charCountColor,
    images, onAddImageCandidates, onRemoveImage,
    footerSlot,
  } = props;

  const isOverwrite = activeTab === 'overwrite';
  const localDraftTitleRef = useRef(draftTitle);
  const localDraftBodyRef = useRef(draftBody);
  const newTitleTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const newBodyTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const titleSyncTimerRef = useRef<number | null>(null);
  const bodySyncTimerRef = useRef<number | null>(null);
  const isEditingNewTitleRef = useRef(false);
  const isEditingNewBodyRef = useRef(false);

  const syncDraftTitleLater = useCallback(
    (next: string) => {
      if (titleSyncTimerRef.current != null) {
        window.clearTimeout(titleSyncTimerRef.current);
      }
      titleSyncTimerRef.current = window.setTimeout(() => {
        titleSyncTimerRef.current = null;
        setDraftTitle(next);
      }, 0);
    },
    [setDraftTitle]
  );

  const syncDraftBodyLater = useCallback(
    (next: string) => {
      if (bodySyncTimerRef.current != null) {
        window.clearTimeout(bodySyncTimerRef.current);
      }
      bodySyncTimerRef.current = window.setTimeout(() => {
        bodySyncTimerRef.current = null;
        setDraftBody(next);
      }, 0);
    },
    [setDraftBody]
  );

  useEffect(() => {
    return () => {
      if (titleSyncTimerRef.current != null) {
        window.clearTimeout(titleSyncTimerRef.current);
      }
      if (bodySyncTimerRef.current != null) {
        window.clearTimeout(bodySyncTimerRef.current);
      }
    };
  }, []);

  const {
    ref: newBodyRef,
    resize: resizeNewBody,
    onResizeStart: onNewBodyResizeStart,
    onResizeEnd: onNewBodyResizeEnd,
  } = useAutoResizeTextarea(draftBody);
  const {
    ref: overwriteBodyRef,
    resize: resizeOverwriteBody,
    onResizeStart: onOverwriteBodyResizeStart,
    onResizeEnd: onOverwriteBodyResizeEnd,
  } = useAutoResizeTextarea(body);

  const setNewTitleTextareaRef = useCallback((node: HTMLTextAreaElement | null) => {
    newTitleTextareaRef.current = node;
    if (node && node.value !== localDraftTitleRef.current) {
      node.value = localDraftTitleRef.current;
    }
  }, []);

  const setNewBodyTextareaRef = useCallback(
    (node: HTMLTextAreaElement | null) => {
      newBodyTextareaRef.current = node;
      newBodyRef(node);
      if (!node) return;
      if (node.value !== localDraftBodyRef.current) {
        node.value = localDraftBodyRef.current;
      }
      resizeNewBody(node);
    },
    [newBodyRef, resizeNewBody]
  );

  useEffect(() => {
    if (activeTab !== 'new') return;
    if (isEditingNewTitleRef.current) return;
    localDraftTitleRef.current = draftTitle;
    if (newTitleTextareaRef.current && newTitleTextareaRef.current.value !== draftTitle) {
      newTitleTextareaRef.current.value = draftTitle;
    }
  }, [activeTab, draftTitle]);

  useEffect(() => {
    if (activeTab !== 'new') return;
    if (isEditingNewBodyRef.current) return;
    localDraftBodyRef.current = draftBody;
    if (newBodyTextareaRef.current && newBodyTextareaRef.current.value !== draftBody) {
      newBodyTextareaRef.current.value = draftBody;
      resizeNewBody(newBodyTextareaRef.current);
    }
  }, [activeTab, draftBody, resizeNewBody]);

  const hasDivision = Boolean(division);

  const [isImageDialogOpen, setIsImageDialogOpen] = useState(false);

  const handleAddImages = useCallback(
    (candidates: ImageAddCandidate[]) => {
      onAddImageCandidates(candidates);
    },
    [onAddImageCandidates]
  );

  const renderImageArea = useCallback(() => {
    return (
      <div className="image-attachments">
        <div className="image-attachments-header">
          <Tooltip title="画像を追加" placement="top">
            <span>
              <Button
                variant="outlined"
                size="small"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => setIsImageDialogOpen(true)}
                startIcon={<AddPhotoAlternateIcon fontSize="small" />}
                sx={{
                  borderRadius: 2,
                  minWidth: 0,
                  px: 1.5,
                  py: 0.5,
                  backgroundColor: 'var(--bg-color)',
                  '&:hover': { backgroundColor: 'var(--bg-color)' },
                }}
              >
                画像を追加
              </Button>
            </span>
          </Tooltip>
        </div>

        <ImageAttachmentsGrid
          images={images}
          onRemoveImage={onRemoveImage}
          editable
        />

        <ImageAddDialog
          open={isImageDialogOpen}
          onClose={() => setIsImageDialogOpen(false)}
          onAdd={handleAddImages}
        />
      </div>
    );
  }, [handleAddImages, images, isImageDialogOpen, onRemoveImage]);

  // ============ NEW タブ用「戻る」E値 ============
  const [originalNewTitle, setOriginalNewTitle] = useState<string>('');
  const [originalNewBody, setOriginalNewBody] = useState<string>('');
  const newInitialCapturedRef = useRef(false);

  useEffect(() => {
    if (!hasDivision) return;
    if (activeTab !== 'new') return;
    if (newInitialCapturedRef.current) return;

    setOriginalNewTitle(draftTitle);
    setOriginalNewBody(draftBody);
    newInitialCapturedRef.current = true;
  }, [activeTab, hasDivision, draftTitle, draftBody]);

  const handleUndoNewTitle = useCallback(() => {
    localDraftTitleRef.current = originalNewTitle;
    if (newTitleTextareaRef.current) {
      newTitleTextareaRef.current.value = originalNewTitle;
    }
    setDraftTitle(originalNewTitle);
  }, [originalNewTitle, setDraftTitle]);

  const handleUndoNewBody = useCallback(() => {
    localDraftBodyRef.current = originalNewBody;
    if (newBodyTextareaRef.current) {
      newBodyTextareaRef.current.value = originalNewBody;
      resizeNewBody(newBodyTextareaRef.current);
    }
    setDraftBody(originalNewBody);
  }, [originalNewBody, resizeNewBody, setDraftBody]);

  const canUndoNewTitle = activeTab === 'new' && draftTitle !== originalNewTitle;
  const canUndoNewBody  = activeTab === 'new' && draftBody  !== originalNewBody;

  // ============ OverwriteEモーダル固定）用「戻る」E値 ============
  const [originalOverwriteTitle, setOriginalOverwriteTitle] = useState<string>('');
  const [originalOverwriteBody, setOriginalOverwriteBody] = useState<string>('');
  const pinnedWrIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isOverwrite) return;
    if (!modalPinned) return;
    if (modalPinnedWrId == null) return;

    if (pinnedWrIdRef.current !== modalPinnedWrId) {
      setOriginalOverwriteTitle(title);
      setOriginalOverwriteBody(body);
      pinnedWrIdRef.current = modalPinnedWrId;
    }
  }, [isOverwrite, modalPinned, modalPinnedWrId, title, body]);

  const handleUndoOverwriteTitle = useCallback(() => {
    setTitle(originalOverwriteTitle);
  }, [originalOverwriteTitle, setTitle]);

  const handleUndoOverwriteBody = useCallback(() => {
    setBody(originalOverwriteBody);
  }, [originalOverwriteBody, setBody]);

  const canUndoOverwriteTitle =
    isOverwrite && modalPinned && title !== originalOverwriteTitle;
  const canUndoOverwriteBody =
    isOverwrite && modalPinned && body !== originalOverwriteBody;

  const dedupedResults = useMemo<SearchResult[]>(
    () => flattenAndDedupByWrId(searchResults as unknown),
    [searchResults]
  );

  const primaryResults = useMemo<SearchResult[]>(() => {
    if (searchPerformed && dedupedResults.length > 0) return dedupedResults;
    if (modalPinned && modalPinnedWrId != null) {
      return dedupedResults.filter((r) => (r?.wr_id ?? r?.WR_ID) === modalPinnedWrId);
    }
    return dedupedResults;
  }, [searchPerformed, dedupedResults, modalPinned, modalPinnedWrId]);

  const { getUid } = useStableUid();

  const normalizeRow = useCallback((r: SearchResult): NormalizedResult => {
    const t = (r.title ?? r.TITLE ?? '') as string;
    const b = (r.body ?? r.BODY ?? '') as string;
    const fbCandidates: unknown[] = [r.feedback_body, r.FEEDBACK_BODY, r.fbNote, r.feedback];
    let feedback = '';
    for (const c of fbCandidates) {
      const got = pickFirstFeedbackText(c);
      if (got) { feedback = got; break; }
    }
    const wrId = (r.wr_id ?? r.WR_ID) as number | undefined;
    const startDate = (r.start_date ?? r.START_DATE ?? '') as string;
    const endDate = (r.end_date ?? r.END_DATE ?? '') as string;
    const createBy = (r.create_by ?? r.CREATE_BY ?? '') as string;
    const uid = getUid(r);
    return { uid, title: t, body: b, feedback, wrId, startDate, endDate, createBy, raw: r };
  }, [getUid]);

  const normalizedResults = useMemo<NormalizedResult[]>(
    () => primaryResults.map(normalizeRow),
    [primaryResults, normalizeRow]
  );

  /** NEWタブ：スピナー用の簡易パルスE状態E親の draftX を唯一の真実とするEE*/
  const savingTitleNew = useSavingPulse(draftTitle);
  const savingBodyNew  = useSavingPulse(draftBody);

  /* Overwrite: 選択対象E親メタ通知対象EE*/
  const [selectedUid, setSelectedUid] = useState<string | null>(null);
  const selectedRow = useMemo(
    () => normalizedResults.find((r) => r.uid === selectedUid) || null,
    [normalizedResults, selectedUid]
  );

  /* 初期選抁E*/
  useEffect(() => {
    if (!isOverwrite) return;
    if (!normalizedResults.length) {
      setSelectedUid(null);
      return;
    }
    if (selectedUid && normalizedResults.some(r => r.uid === selectedUid)) return;
    setSelectedUid(normalizedResults[0].uid);
  }, [isOverwrite, normalizedResults, selectedUid]);

  /* 選択変更 -> 親にメタ通知 */
  useEffect(() => {
    if (!isOverwrite) return;
    if (!selectedRow) return;
    if (selectedRow.wrId !== undefined) setWrId(selectedRow.wrId);
    if (selectedRow.startDate) setStartDate(selectedRow.startDate);
    if (selectedRow.endDate) setEndDate(selectedRow.endDate);
    if (selectedRow.createBy) setReporter(selectedRow.createBy);
  }, [isOverwrite, selectedRow, setWrId, setStartDate, setEndDate, setReporter]);

  const activateUid   = useCallback((uid: string) => setSelectedUid(uid), []);
  const onCommitTitle = useCallback((v: string) => { setTitle(v); }, [setTitle]);
  const onCommitBody  = useCallback((v: string) => { setBody(v);  }, [setBody]);

  const SyncIcon = useMemo(
    () =>
      enlightSyncEnabled
        ? <CheckCircleIcon sx={{ fontSize: 16, verticalAlign: 'middle' }} color="success" />
        : <CheckCircleOutlineIcon sx={{ fontSize: 16, verticalAlign: 'middle' }} color="disabled" />,
    [enlightSyncEnabled]
  );
  const SpinnerOrSync = useCallback(
    (spinning: boolean) => (spinning ? <CircularProgress size={14} sx={{ ml: 0.5 }} /> : SyncIcon),
    [SyncIcon]
  );

  const charColor =
    charCountColor === 'hazard'
      ? '#f44336'
      : charCountColor === 'warning'
      ? '#ffb300'
      : 'inherit';

  return (
    <div>
      {/* NEW */}
      {hasDivision && activeTab === 'new' && (
        <div className="content-wrapper">
          <div className="content-card">
            <div className="content-grid">
              <div className="content-main">
                <div className="form-field">
                  <div className="start-date-icon">
                    <label className="required-label">タイトル</label>
                    &nbsp;{SpinnerOrSync(savingTitleNew)}
                    <Tooltip
                      title="入力内容すべて戻します"
                      placement="top"
                    >
                      <span>
                        <IconButton
                          size="small"
                          style={{ margin: 0 }}
                          sx={{
                            color: canUndoNewTitle
                              ? 'primary.main'
                              : 'action.disabled',
                          }}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUndoNewTitle();
                          }}
                          disabled={!canUndoNewTitle}
                        >
                          <UndoIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </div>
                  {/* タイトルめE行textareaに変更 */}
                  <textarea
                    id="title-textarea"
                    rows={2}
                    className="title-textarea search-result-input"
                    ref={setNewTitleTextareaRef}
                    defaultValue={draftTitle}
                    onFocus={(e) => {
                      isEditingNewTitleRef.current = true;
                      localDraftTitleRef.current = e.currentTarget.value;
                    }}
                    onBlur={(e) => {
                      isEditingNewTitleRef.current = false;
                      const next = e.currentTarget.value;
                      localDraftTitleRef.current = next;
                      setDraftTitle(next);
                    }}
                    onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
                      const next = e.target.value;
                      localDraftTitleRef.current = next;
                      syncDraftTitleLater(next);
                    }}
                  />
                </div>

                <div className="form-field">
                  <div className="start-date-icon">
                    <label className="required-label">本文</label>
                    &nbsp;{SpinnerOrSync(savingBodyNew)}
                    <Tooltip
                      title="入力内容すべて戻します"
                      placement="top"
                    >
                      <span>
                        <IconButton
                          size="small"
                          style={{ margin: 0 }}
                          sx={{
                            color: canUndoNewBody
                              ? 'primary.main'
                              : 'action.disabled',
                          }}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUndoNewBody();
                          }}
                          disabled={!canUndoNewBody}
                        >
                          <UndoIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <span style={{ marginLeft: 8, fontWeight: 600, color: charColor }}>
                      {remainingChars >= 0 ? '残り ' : '文字数超過 '}
                      <span>{remainingChars}</span>
                    </span>
                  </div>
                  <textarea
                    className="body-textarea search-result-textarea"
                    rows={5}
                    ref={setNewBodyTextareaRef}
                    onPointerDown={onNewBodyResizeStart}
                    onPointerUp={onNewBodyResizeEnd}
                    defaultValue={draftBody}
                    onFocus={(e) => {
                      isEditingNewBodyRef.current = true;
                      localDraftBodyRef.current = e.currentTarget.value;
                    }}
                    onBlur={(e) => {
                      isEditingNewBodyRef.current = false;
                      const next = e.currentTarget.value;
                      localDraftBodyRef.current = next;
                      setDraftBody(next);
                    }}
                    onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
                      const next = e.target.value;
                      localDraftBodyRef.current = next;
                      syncDraftBodyLater(next);
                      resizeNewBody(e.currentTarget);
                    }}
                  />
                </div>

              </div>

              <div className="content-side">
                <div className="form-field">
                  <div className="start-date-icon-fb">
                    <label>F/B事項</label>
                  </div>
                  <textarea
                    className="fb-textarea search-result-textarea"
                    rows={5}
                    value={fbNote}
                    readOnly
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="content-image-actions-row">
            <div className="content-image-row">
              {renderImageArea()}
            </div>
            {footerSlot && (
              <div className={`content-actions-row${isOverwrite ? ' content-actions-row--old' : ''}`}>
                {footerSlot}
              </div>
            )}
          </div>
        </div>
      )}

      {/* OverwriteEモーダル固定（検索0件時！E*/}
      {normalizedResults.length === 0 && isOverwrite && modalPinned && (
        <div className="content-wrapper">
          <div className="content-card">
            <div className="content-grid">
              <div className="content-main">
                <div className="form-field">
                  <div className="start-date-icon">
                    <label className="required-label">タイトル</label>
                    &nbsp;{SpinnerOrSync(false)}
                    <Tooltip
                      title="入力内容すべて戻します"
                      placement="top"
                    >
                      <span>
                        <IconButton
                          style={{ margin: 0 }}
                          size="small"
                          sx={{
                            color: canUndoOverwriteTitle
                              ? 'primary.main'
                              : 'action.disabled',
                          }}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUndoOverwriteTitle();
                          }}
                          disabled={!canUndoOverwriteTitle}
                        >
                          <UndoIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </div>
                  {/* タイトルめE行textareaに変更 */}
                  <textarea
                    id="title-textarea"
                    rows={2}
                    className="title-textarea search-result-input"
                    value={title}
                    onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
                      setTitle(e.target.value);
                    }}
                  />
                </div>

                <div className="form-field">
                  <div className="start-date-icon">
                    <label className="required-label">本文</label>
                    &nbsp;{SpinnerOrSync(false)}
                    <Tooltip
                      title="入力内容すべて戻します"
                      placement="top"
                    >
                      <span>
                        <IconButton
                          style={{ margin: 0 }}
                          size="small"
                          sx={{
                            color: canUndoOverwriteBody
                              ? 'primary.main'
                              : 'action.disabled',
                          }}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUndoOverwriteBody();
                          }}
                          disabled={!canUndoOverwriteBody}
                        >
                          <UndoIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <span style={{ marginLeft: 8, fontWeight: 600, color: charColor }}>
                      {remainingChars >= 0 ? '残り ' : '文字数超過 '}
                      <span>{remainingChars}</span>
                    </span>
                  </div>
                  <textarea
                    className="body-textarea search-result-textarea"
                    rows={5}
                    ref={overwriteBodyRef}
                    onPointerDown={onOverwriteBodyResizeStart}
                    onPointerUp={onOverwriteBodyResizeEnd}
                    value={body}
                    onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
                      setBody(e.target.value);
                      resizeOverwriteBody(e.currentTarget);
                    }}
                  />
                </div>

              </div>

              <div className="content-side">
                <div className="form-field">
                  <div className="start-date-icon-fb">
                    <label>F/B事項</label>
                  </div>
                  <textarea
                    className="fb-textarea search-result-textarea"
                    rows={5}
                    value={fbNote}
                    readOnly
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="content-image-actions-row">
            <div className="content-image-row">
              {renderImageArea()}
            </div>
            {footerSlot && (
              <div className={`content-actions-row${isOverwrite ? ' content-actions-row--old' : ''}`}>
                {footerSlot}
              </div>
            )}
          </div>
        </div>
      )}

      {/* OverwriteE褁EヒッチE*/}
      {normalizedResults.length > 0 && isOverwrite && (
        <div className="content-wrapper">
          <div className="content-list">
            {normalizedResults.map((r) => (
              <OverwriteCard
                key={r.uid}
                result={r}
                active={selectedUid === r.uid}
                onActivate={() => activateUid(r.uid)}
                onCommitTitle={(v) => { activateUid(r.uid); onCommitTitle(v); }}
                onCommitBody={(v)  => { activateUid(r.uid); onCommitBody(v);  }}
                SpinnerOrSync={SpinnerOrSync}
                remainingChars={remainingChars}
                charCountColor={charCountColor}
              />
            ))}
          </div>
          {footerSlot && (
            <div className={`content-actions-row content-actions-row--solo${isOverwrite ? ' content-actions-row--old' : ''}`}>
              {footerSlot}
            </div>
          )}
        </div>
      )}

      {/* 検索結果なし（モーダル固定でもなく「検索実行済み」EときEみEE*/}
      {normalizedResults.length === 0 &&
        isOverwrite &&
        !modalPinned &&
        searchPerformed && (
          <div className="no-results">
            <div className="no-results-content">
              <InfoOutlinedIcon sx={{ fontSize: 32, color: '#4a90e2' }} />
              <h3>検索結果がありませんでした</h3>
            </div>
          </div>
        )}
    </div>
  );
};

export default Content;
